/**
 * Your own account: how you signed in, and what that lets you do.
 *
 * There is nothing to change here. Identity is your Google account, so your
 * password, two-factor settings and session security live in Google — this page
 * says so and points there rather than offering a second, weaker copy of those
 * controls. Your role is not yours to change either, by design.
 */
import { ROLE_LABELS, ROLE_PERMISSIONS, PERMISSION_META } from '@accounting/shared'
import { useSession } from '../lib/session'
import { RoleBadge } from '../ui/Badge'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { PageHeader } from '../ui/PageHeader'
import { NotificationPreferences } from '../components/NotificationPreferences'
import { CheckIcon, LockIcon } from '../ui/icons'
import type { ReactNode } from 'react'

const GOOGLE_ACCOUNT_SECURITY = 'https://myaccount.google.com/security'

export function Account() {
  const { user } = useSession()
  if (!user) return null

  const domain = user.email.split('@')[1] ?? ''

  return (
    <>
      <PageHeader
        title="Your account"
        description="Your role decides what you can reach. Ask Finance for a change — a role is not something an account can grant itself."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card>
            <CardHeader title="Details" />
            <CardBody className="space-y-3 text-sm">
              <Detail label="Name">{user.fullName}</Detail>
              <Detail label="Email">{user.email}</Detail>
              <Detail label="Role">
                <RoleBadge role={user.role} />
              </Detail>
              <Detail label="Signed in with">Google{domain && ` · ${domain}`}</Detail>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Sign-in and security" />
            <CardBody className="text-sm leading-relaxed text-ink-700">
              <p className="flex items-start gap-2.5">
                <LockIcon className="mt-0.5 shrink-0 text-brand-700" />
                <span>
                  This platform stores no password for you. Your password, two-step verification and
                  the list of devices signed in are managed on your Google account.
                </span>
              </p>

              <p className="mt-4">
                <a
                  href={GOOGLE_ACCOUNT_SECURITY}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-medium text-brand-700 underline"
                >
                  Google account security
                </a>
              </p>

              <p className="mt-4 text-xs text-ink-600">
                Sessions here last twelve hours and end when you sign out. Signing out of Google does
                not end a session already running here, and deactivating an account does — Finance
                does that from the Users screen.
              </p>
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardHeader
            title="What you may do"
            description={`${ROLE_PERMISSIONS[user.role].length} grants from the ${ROLE_LABELS[user.role]} role`}
          />
          <CardBody>
            <ul className="space-y-2">
              {ROLE_PERMISSIONS[user.role].map((permission) => (
                <li key={permission} className="flex items-start gap-2 text-sm">
                  <CheckIcon className="mt-0.5 shrink-0 text-brand-700" />
                  <span>
                    <span className="block text-ink-900">{PERMISSION_META[permission].label}</span>
                    <span className="block text-xs text-ink-600">
                      {PERMISSION_META[permission].description}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>

      {/**
        * Notification settings sit here rather than under Administration
        * because they are settings about *you*, not about the firm. The alert
        * thresholds — which are the firm's — live behind `alerts.manage` on
        * their own screen. Somebody with no alert grants still reaches this
        * one; their list of alert types is simply empty, and it says so.
        */}
      <div className="mt-6">
        <NotificationPreferences />
      </div>
    </>
  )
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <span className="text-ink-600">{label}</span>
      <span className="text-right font-medium text-ink-900">{children}</span>
    </div>
  )
}
